import React, { useCallback, useEffect, useState } from "react";
import { Modal, notification } from "antd";
import { useTranslation } from "react-i18next";
import Representation from "./Representation";
import {
  addDoNotCall,
  createCrmKey,
  crmWebhookUrl,
  fetchCrmConnection,
  fetchDoNotCall,
  removeDoNotCall,
  revokeCrmKey,
} from "util/crmConnectionApi";
import { copyTextToClipboard } from "util/mcpSetup";

const K = "crmConnection";

/**
 * Settings → Integrations → CRM connection: the company's one CRM key (shown once when made),
 * the webhook address, the surveys a CRM can call and the latest events. Admins manage it;
 * other members are told who does.
 */
const CrmView = ({ onBack }) => {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [surveyId, setSurveyId] = useState(null);
  // The do-not-call list: null until loaded (company admins only).
  const [dnc, setDnc] = useState(null);
  const [dncBusy, setDncBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchCrmConnection();
    setLoading(false);
    if (!res || res.cancel) return;
    if (res.error) {
      setInfo({ error: true, notReady: res.database_ready === false });
      return;
    }
    setInfo(res);
    const surveys = res.surveys || [];
    setSurveyId((current) => current ?? (surveys.find((s) => s.trigger_on) || surveys[0])?.id ?? null);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadDnc = useCallback(async () => {
    const res = await fetchDoNotCall();
    if (res && !res.error && !res.cancel) setDnc(res.numbers || []);
  }, []);

  useEffect(() => {
    if (info?.can_manage) loadDnc();
  }, [info?.can_manage, loadDnc]);

  const onAddDnc = async (phone) => {
    setDncBusy(true);
    const res = await addDoNotCall(phone);
    setDncBusy(false);
    if (res?.numbers) {
      setDnc(res.numbers);
      notification.success({ message: t(`${K}.dnc_added`) });
      return true;
    }
    if (!res?.cancel) {
      notification.error({ message: t(res?.error === "invalid_phone" ? `${K}.dnc_invalid` : `${K}.error`) });
    }
    return false;
  };

  const onRemoveDnc = (row) =>
    Modal.confirm({
      title: t(`${K}.dnc_remove_title`),
      content: t(`${K}.dnc_remove_body`, { phone: row.phone }),
      okText: t(`${K}.dnc_remove`),
      cancelText: t(`${K}.cancel`),
      onOk: async () => {
        const res = await removeDoNotCall(row.ref);
        if (res?.numbers) {
          setDnc(res.numbers);
          notification.success({ message: t(`${K}.dnc_removed`) });
        } else if (!res?.cancel) {
          notification.error({ message: t(`${K}.error`) });
        }
      },
    });

  const copy = async (text) => {
    if (await copyTextToClipboard(text)) notification.success({ message: t(`${K}.copied`) });
  };

  const makeKey = async () => {
    setBusy(true);
    const res = await createCrmKey();
    setBusy(false);
    if (res?.key) {
      setNewKey(res.key);
      load();
    } else if (!res?.cancel) {
      notification.error({ message: t(`${K}.error`) });
    }
  };

  const onCreate = () => {
    if (info?.key) {
      Modal.confirm({
        title: t(`${K}.rotate_title`),
        content: t(`${K}.rotate_body`),
        okText: t(`${K}.rotate`),
        cancelText: t(`${K}.cancel`),
        onOk: makeKey,
      });
    } else {
      makeKey();
    }
  };

  const onRevoke = () =>
    Modal.confirm({
      title: t(`${K}.revoke_title`),
      content: t(`${K}.revoke_body`),
      okText: t(`${K}.revoke`),
      cancelText: t(`${K}.cancel`),
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await revokeCrmKey();
        if (res && !res.error && !res.cancel) {
          setNewKey(null);
          notification.success({ message: t(`${K}.revoked`) });
          load();
        } else if (!res?.cancel) {
          notification.error({ message: t(`${K}.error`) });
        }
      },
    });

  return (
    <Representation
      t={t}
      info={info}
      loading={loading}
      busy={busy}
      newKey={newKey}
      url={crmWebhookUrl()}
      surveyId={surveyId}
      onSurveyChange={setSurveyId}
      onBack={onBack}
      onCreate={onCreate}
      onRevoke={onRevoke}
      onReload={load}
      onCopy={copy}
      dnc={dnc}
      dncBusy={dncBusy}
      onAddDnc={onAddDnc}
      onRemoveDnc={onRemoveDnc}
    />
  );
};

export default CrmView;
